import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatError } from "../api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, BookOpen, Clock, FileText, PlayCircle, GraduationCap, ShieldCheck } from "lucide-react";
import BuddyChat from "../components/BuddyChat";
import PublicNavBar from "../components/PublicNavBar";
import PublicFooter from "../components/PublicFooter";
import { getCoeExternalUrl } from "../lib/coeExternalUrls";

export default function LMSLab() {
  const { labId } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [lab, setLab] = useState(null);
  const [enr, setEnr] = useState(null);
  const [msg, setMsg] = useState("");
  const [redirecting, setRedirecting] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/lms/labs/${labId}`);
      setLab(r.data);
      if (user?.role === "student") {
        const r2 = await api.get("/me/lms/enrollments");
        setEnr(r2.data.find(e => e.lab_id === labId) || null);
      }
    } catch (e) { setMsg(formatError(e)); }
  }, [labId, user?.role]);
  useEffect(() => { load(); }, [load]);

  // If this lab has an external partner-LMS URL, redirect the student
  // straight to the partner via the SSO handoff. Keeps the deep-link
  // consistent even when a user lands here from a stale bookmark or
  // the browser back button.
  useEffect(() => {
    if (!lab || redirecting) return;
    const code = lab.lab_code || lab.code;
    const extUrl = getCoeExternalUrl(code);
    if (!extUrl) return;
    if (!user) {
      // Deep-linked while logged out — send them through login first;
      // login page will finish the SSO handoff.
      setRedirecting(true);
      nav(`/login?sso_lab=${encodeURIComponent(code)}`, { replace: true });
      return;
    }
    setRedirecting(true);
    (async () => {
      try {
        const { data } = await api.post("/sso/issue-handoff", { lab_code: code });
        setTimeout(() => { window.location.href = data.redirect_url; }, 250);
      } catch (_) {
        // Fallback — hit the bare partner URL; student sees partner login.
        window.location.href = extUrl;
      }
    })();
  }, [lab, user, nav, redirecting]);

  const enroll = async () => {
    if (!user) { nav("/login"); return; }
    if (user.role !== "student") { setMsg("Only students can enrol."); return; }
    try {
      await api.post(`/lms/labs/${labId}/enroll`);
      load();
    } catch (e) { setMsg(formatError(e)); }
  };

  if (!lab) return <div className="min-h-screen bg-paper p-10 text-muted">Loading…</div>;
  if (redirecting) return (
    <div className="min-h-screen bg-paper p-10 text-muted flex items-center justify-center" data-testid="lab-sso-redirecting">
      Signing you into {lab?.lab_code || "the partner LMS"}…
    </div>
  );

  const lessonIcon = (t) => t === "video" ? PlayCircle : t === "pdf" ? FileText : BookOpen;

  return (
    <div className="min-h-screen bg-paper">
      <PublicNavBar />
      {/* Sub-bar: back link */}
      <div className="border-b border-rule bg-white">
        <div className="max-w-6xl mx-auto px-6 py-2.5 flex items-center justify-between">
          <Link to={user?.role === "student" ? "/student/lms" : "/lms"} className="inline-flex items-center gap-2 text-sm font-semibold text-signal" data-testid="back-to-lms">
            <ArrowLeft size={14} /> Back to labs
          </Link>
          <span className="text-xs uppercase tracking-widest font-mono text-muted">{lab.lab_code}</span>
        </div>
      </div>

      <section className="relative border-b border-rule overflow-hidden" style={{ background: `linear-gradient(120deg, ${lab.color} 0%, ${lab.color}cc 100%)` }}>
        {lab.cover_image && (
          <>
            <img src={lab.cover_image} alt="" aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: `linear-gradient(110deg, ${lab.color}f0 0%, ${lab.color}c0 55%, ${lab.color}55 100%)` }} />
          </>
        )}
        <div className="relative max-w-6xl mx-auto px-6 md:px-8 py-12 text-white">
          <span className="font-mono text-xs tracking-widest opacity-80">{lab.category}</span>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <span className="font-mono text-sm bg-white/15 border border-white/30 px-2 py-1 rounded-sm" data-testid="lab-code">{lab.code}</span>
            <h1 className="font-display font-extrabold text-3xl md:text-5xl leading-tight" data-testid="lab-name">{lab.name}</h1>
          </div>
          {lab.tagline && <p className="text-lg md:text-xl opacity-95 max-w-3xl mt-4 italic font-medium">{lab.tagline}</p>}
          <p className="text-base md:text-lg opacity-85 max-w-3xl mt-3 leading-relaxed">{lab.overview || lab.description}</p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            {(lab.skills || []).map(s => (
              <span key={s} className="bg-white/15 border border-white/30 px-3 py-1 text-xs font-mono rounded-sm">{s}</span>
            ))}
          </div>
          {lab.stats && lab.stats.length > 0 && (
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
              {lab.stats.map(([k, v]) => (
                <div key={`stat-${k}`} className="bg-white/10 border border-white/20 px-4 py-3 rounded-sm">
                  <div className="font-display font-extrabold text-2xl">{k}</div>
                  <div className="text-xs opacity-80 mt-1">{v}</div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-8 flex flex-wrap gap-3 items-center" data-testid="enroll-area">
            {enr ? (
              <>
                <span className="bg-white text-ink px-4 py-2 rounded-sm font-semibold inline-flex items-center gap-2">
                  <GraduationCap size={16} /> Enrolled · {enr.progress_pct}% complete
                </span>
                {enr.status === "completed" && (
                  <span className="bg-black/30 border border-white/40 px-3 py-2 rounded-sm text-sm inline-flex items-center gap-2">
                    <ShieldCheck size={14} /> Lab completed
                  </span>
                )}
              </>
            ) : (
              <button onClick={enroll} className="bg-white text-ink px-5 py-3 rounded-sm font-semibold inline-flex items-center gap-2 hover:bg-paper hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200" data-testid="enroll-btn">
                Enrol in {lab.code}
              </button>
            )}
            {msg && <span className="text-sm opacity-90" data-testid="lab-msg">{msg}</span>}
          </div>
        </div>
      </section>

      {/* Content blocks: focus, equipment, outcomes, partners */}
      {(lab.focus_areas?.length || lab.equipment?.length || lab.outcomes?.length || lab.partners?.length) > 0 && (
        <section className="bg-white border-b border-rule" data-testid="lab-info-blocks">
          <div className="max-w-6xl mx-auto px-6 md:px-8 py-12 grid md:grid-cols-2 gap-x-12 gap-y-10">
            {lab.focus_areas?.length > 0 && (
              <div>
                <span className="eyebrow" style={{ color: lab.color }}>Focus areas</span>
                <h3 className="font-display font-bold text-xl mt-2 mb-4">What you&apos;ll work on</h3>
                <ul className="space-y-2">
                  {lab.focus_areas.map((f) => (
                    <li key={`focus-${f}`} className="flex gap-3 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: lab.color }} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {lab.equipment?.length > 0 && (
              <div>
                <span className="eyebrow" style={{ color: lab.color }}>Equipment & toolchain</span>
                <h3 className="font-display font-bold text-xl mt-2 mb-4">Industry-grade infrastructure</h3>
                <ul className="space-y-2">
                  {lab.equipment.map((e) => (
                    <li key={`equip-${e}`} className="flex gap-3 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: lab.color }} />
                      <span>{e}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {lab.outcomes?.length > 0 && (
              <div>
                <span className="eyebrow" style={{ color: lab.color }}>Learner outcomes</span>
                <h3 className="font-display font-bold text-xl mt-2 mb-4">Skills & certifications you walk away with</h3>
                <ul className="space-y-2">
                  {lab.outcomes.map((o) => (
                    <li key={`outcome-${o}`} className="flex gap-3 text-sm">
                      <span className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: lab.color }} />
                      <span>{o}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {lab.partners?.length > 0 && (
              <div>
                <span className="eyebrow" style={{ color: lab.color }}>Industry partners</span>
                <h3 className="font-display font-bold text-xl mt-2 mb-4">Where this lab places talent</h3>
                <div className="flex flex-wrap gap-2">
                  {lab.partners.map((p) => (
                    <span key={`partner-${p}`} className="px-3 py-1.5 text-xs rounded-sm border"
                      style={{ borderColor: `${lab.color}30`, background: `${lab.color}08`, color: lab.color }}>
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      <section className="max-w-6xl mx-auto px-6 md:px-8 py-12">
        <div className="flex items-end justify-between mb-6">
          <div>
            <span className="eyebrow">Curriculum</span>
            <h2 className="font-display font-bold text-2xl md:text-3xl mt-1">{lab.modules?.length || 0} modules</h2>
          </div>
          <span className="text-sm text-muted">Auto-graded MCQ at end of every module</span>
        </div>
        <div className="space-y-4">
          {(lab.modules || []).map((m, i) => (
            <div key={m.id} className="card card-pad" data-testid={`module-${m.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                  <span className="eyebrow">Module {i + 1}</span>
                  <h3 className="font-display font-bold text-xl mt-1">{m.title}</h3>
                  <p className="text-sm text-muted mt-1 max-w-2xl">{m.description}</p>
                  <div className="text-xs text-muted mt-2 inline-flex items-center gap-1">
                    <Clock size={12} /> {m.duration_hours}h · {m.lessons?.length} lessons
                  </div>
                </div>
                {m.assessment && (
                  <span className="badge badge-signal">Quiz · {m.assessment.question_count} Qs · pass {m.assessment.pass_score}%</span>
                )}
              </div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(m.lessons || []).map(ls => {
                  const Icon = lessonIcon(ls.type);
                  const inner = (
                    <div className="flex items-center gap-3 px-3 py-2 border border-rule rounded-sm hover:border-signal transition bg-paper min-w-0">
                      <Icon size={16} className="text-signal shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{ls.title}</div>
                        <div className="text-xs text-muted">{ls.type.toUpperCase()} · {ls.duration_min}m</div>
                      </div>
                    </div>
                  );
                  return enr ? (
                    <Link key={ls.id} to={`/student/lms/lessons/${ls.id}`} className="min-w-0 block" data-testid={`lesson-${ls.id}`}>{inner}</Link>
                  ) : (
                    <div key={ls.id} className="opacity-60 cursor-not-allowed min-w-0" data-testid={`lesson-locked-${ls.id}`}>{inner}</div>
                  );
                })}
              </div>
              {enr && m.assessment && (
                <div className="mt-4">
                  <Link to={`/student/lms/assessments/${m.assessment.id}`}
                    className="btn-secondary text-sm" data-testid={`take-quiz-${m.assessment.id}`}>
                    Take module quiz →
                  </Link>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <BuddyChat lab={lab} />
      <PublicFooter compact />
    </div>
  );
}
